import { useState } from "react";
import { Box, Typography, Alert, Snackbar } from "@mui/material";
import { apiPost } from "../../services/genesisApi/httpsMethods";
import { CustomButton } from "../../components/common/customButton";
import { SimpleToolbar } from "../../components/layout/Toolbar";
import ReusableFileExtractor, {
  type ExtractedTenant,
} from "../../components/common/ReusableFileExtractor";
import type { Asset, LoanComp } from "../../types/deal";
import ExtractedAssetsTable from "../assets/ExtractedAssetsTable";
import ExtractedTenantsTable from "../tenant/ExtractedTenantTable";
import ExtractedLoanCompsTable from "../loanComps/ExtractedLoanCompsTable";
import { useDealProvider } from "../../context/DealContext";
import { buildLoanCompPayload } from "../loanComps/loanCompPayload";

const UploadComps = () => {
  const [extractedAssets, setExtractedAssets] = useState<Partial<Asset>[]>([]);
  const [extractedTenants, setExtractedTenants] = useState<ExtractedTenant[]>([]);
  const [extractedLoanComps, setExtractedLoanComps] = useState<
    Partial<LoanComp>[]
  >([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState<{
    assets: { success: number; failed: number };
    tenants: { success: number; failed: number };
    loanComps: { success: number; failed: number };
  } | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const { setAssets, setTenants, setLoanComps } = useDealProvider();

  const handleAssetsExtracted = (assets: Partial<Asset>[]) => {
    setExtractedAssets(assets);
    setAssets(assets);
  };

  const handleTenantsExtracted = (tenants: ExtractedTenant[]) => {
    setExtractedTenants(tenants);
    setTenants(tenants);
  };

  const handleLoanCompsExtracted = (loanComps: Partial<LoanComp>[]) => {
    setExtractedLoanComps(loanComps);
    setLoanComps(loanComps);
  };

  const uploadData = async (
    data: any[],
    endpoint: string,
    dataType: string,
  ) => {
    const results = { success: 0, failed: 0 };

    for (const item of data) {
      try {
        const response = await apiPost(endpoint, item);
        if (response.success) {
          results.success++;
        } else {
          results.failed++;
          console.error(`Failed to upload ${dataType}:`, response.message);
        }
      } catch (error) {
        results.failed++;
        console.error(`Error uploading ${dataType}:`, error);
      }
    }

    return results;
  };

  const handleUploadAll = async () => {
    if (
      extractedAssets.length === 0 &&
      extractedTenants.length === 0 &&
      extractedLoanComps.length === 0
    ) {
      setSnackbarMessage(
        "No data to upload. Please extract data from Excel file first.",
      );
      setSnackbarOpen(true);
      return;
    }

    setIsUploading(true);
    setUploadResults(null);

    try {
      const results = {
        assets: { success: 0, failed: 0 },
        tenants: { success: 0, failed: 0 },
        loanComps: { success: 0, failed: 0 },
      };

      // Upload assets
      if (extractedAssets.length > 0) {
        results.assets = await uploadData(extractedAssets, "assets", "assets");
      }

      // Upload tenants occurs on asset upload

      // Upload loan comps
      if (extractedLoanComps.length > 0) {
        // POST only real loan_comps columns (country/sector objects -> *_id);
        // the backend create rejects unknown keys.
        results.loanComps = await uploadData(
          extractedLoanComps.map((loanComp) => buildLoanCompPayload(loanComp)),
          "loan-comps",
          "loan comps",
        );
      }

      setUploadResults(results);

      const totalSuccess =
        results.assets.success +
        results.tenants.success +
        results.loanComps.success;
      const totalFailed =
        results.assets.failed +
        results.tenants.failed +
        results.loanComps.failed;

      if (totalFailed === 0) {
        setSnackbarMessage(
          `Successfully uploaded all data! ${totalSuccess} records processed.`,
        );
      } else {
        setSnackbarMessage(
          `Upload completed with some errors. ${totalSuccess} successful, ${totalFailed} failed.`,
        );
      }
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error during upload:", error);
      setSnackbarMessage("An error occurred during upload. Please try again.");
      setSnackbarOpen(true);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClearData = () => {
    setExtractedAssets([]);
    setExtractedTenants([]);
    setExtractedLoanComps([]);
    setUploadResults(null);
  };

  const getTotalExtractedCount = () => {
    return (
      extractedAssets.length +
      extractedTenants.length +
      extractedLoanComps.length
    );
  };

  return (
    <Box sx={{ position: "relative", zIndex: 1 }}>
      <Box sx={{ position: "sticky", zIndex: 10, marginBottom: "10px" }}>
        <SimpleToolbar>
          <CustomButton
            text="Upload All Data"
            handleButtonPress={handleUploadAll}
            isButtonDisabled={isUploading || getTotalExtractedCount() === 0}
            solid={true}
            data-position="center"
          />
          <CustomButton
            text="Clear Data"
            handleButtonPress={handleClearData}
            isButtonDisabled={getTotalExtractedCount() === 0}
            solid={false}
            data-position="right"
          />
        </SimpleToolbar>
      </Box>

      <Box sx={{ width: "95%", margin: "0 auto", padding: 4 }}>
        <ReusableFileExtractor
          mode="comps"
          onAssetsExtracted={handleAssetsExtracted}
          onTenantsExtracted={handleTenantsExtracted}
          onLoanCompsExtracted={handleLoanCompsExtracted}
        />

        {/* Data Summary */}
        {(extractedAssets.length > 0 ||
          extractedTenants.length > 0 ||
          extractedLoanComps.length > 0) && (
          <Box
            sx={{ mt: 4, p: 3, backgroundColor: "#f5f5f5", borderRadius: 2 }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Extracted Data Summary
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 3,
                flexWrap: "wrap",
                flexDirection: "column",
              }}
            >
              <Box>
                <ExtractedLoanCompsTable />
              </Box>
              <Box>
                <ExtractedAssetsTable dealId={null} />
              </Box>
              <Box>
                <ExtractedTenantsTable assetId={null} editMode={true} />
              </Box>
            </Box>
          </Box>
        )}

        {/* Upload Results */}
        {uploadResults && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Upload Results
            </Typography>

            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              {uploadResults.loanComps.success > 0 ||
              uploadResults.loanComps.failed > 0 ? (
                <Alert
                  severity={
                    uploadResults.loanComps.failed === 0 ? "success" : "warning"
                  }
                  sx={{ flex: "1 1 200px" }}
                >
                  Loan Comps: {uploadResults.loanComps.success} successful,{" "}
                  {uploadResults.loanComps.failed} failed
                </Alert>
              ) : null}

              {uploadResults.assets.success > 0 ||
              uploadResults.assets.failed > 0 ? (
                <Alert
                  severity={
                    uploadResults.assets.failed === 0 ? "success" : "warning"
                  }
                  sx={{ flex: "1 1 200px" }}
                >
                  Assets: {uploadResults.assets.success} successful,{" "}
                  {uploadResults.assets.failed} failed
                </Alert>
              ) : null}

              {uploadResults.tenants.success > 0 ||
              uploadResults.tenants.failed > 0 ? (
                <Alert
                  severity={
                    uploadResults.tenants.failed === 0 ? "success" : "warning"
                  }
                  sx={{ flex: "1 1 200px" }}
                >
                  Tenants: {uploadResults.tenants.success} successful,{" "}
                  {uploadResults.tenants.failed} failed
                </Alert>
              ) : null}
            </Box>
          </Box>
        )}
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default UploadComps;
